import type { GameSnapshot } from '../types/core';
import { useGameStore } from '../simulation/store';
import { createInitialGameState } from '../world/createInitialState';

const DB_NAME = 'the-bees-hive';
const DB_VERSION = 1;
const STORE_NAME = 'saves';
const SAVE_KEY = 'autosave';
const SETTINGS_KEY = 'the-bees-hive.settings';

let autoSaveTimer: number | undefined;
let queuedSnapshot: GameSnapshot | undefined;

interface LocalSettings {
  readonly hasSeenWelcome: boolean;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

function readSettings(): LocalSettings {
  const raw = safeLocalStorageGet(SETTINGS_KEY);
  if (!raw) {
    return { hasSeenWelcome: false };
  }

  try {
    return JSON.parse(raw) as LocalSettings;
  } catch {
    return { hasSeenWelcome: false };
  }
}

function writeSettings(settings: LocalSettings): void {
  safeLocalStorageSet(SETTINGS_KEY, JSON.stringify(settings));
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Settings are a convenience; the game should still run if storage is blocked.
  }
}

async function putSnapshot(snapshot: GameSnapshot): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(snapshot, SAVE_KEY);
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();
  });
  db.close();
}

async function getSnapshot(): Promise<GameSnapshot | undefined> {
  const db = await openDb();
  const snapshot = await new Promise<GameSnapshot | undefined>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const request = transaction.objectStore(STORE_NAME).get(SAVE_KEY);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as GameSnapshot | undefined);
  });
  db.close();
  return snapshot;
}

export async function loadGame(): Promise<void> {
  const settings = readSettings();

  try {
    const saved = await getSnapshot();

    if (saved?.version === 1) {
      useGameStore.getState().replaceFromSnapshot({
        ...saved,
        state: {
          ...saved.state,
          hasSeenWelcome: settings.hasSeenWelcome || saved.state.hasSeenWelcome
        }
      });
      return;
    }
  } catch (error) {
    console.warn('Save loading failed; starting a new town', error);
  }

  useGameStore.getState().hydrate({
    ...createInitialGameState(),
    hydrated: true,
    hasSeenWelcome: settings.hasSeenWelcome
  });
}

export function scheduleAutoSave(snapshot: GameSnapshot): void {
  if (!snapshot.state.hydrated) {
    return;
  }

  queuedSnapshot = snapshot;

  if (autoSaveTimer !== undefined) {
    return;
  }

  autoSaveTimer = window.setTimeout(() => {
    const nextSnapshot = queuedSnapshot;
    queuedSnapshot = undefined;
    autoSaveTimer = undefined;

    if (!nextSnapshot) {
      return;
    }

    writeSettings({ hasSeenWelcome: nextSnapshot.state.hasSeenWelcome });
    void putSnapshot(nextSnapshot).catch((error: unknown) => {
      console.warn('Auto-save failed', error);
    });
  }, 2400);
}

export function startAutoSave(): () => void {
  return useGameStore.subscribe((state) => {
    scheduleAutoSave(state.snapshot());
  });
}

export async function manualSave(): Promise<void> {
  const snapshot = useGameStore.getState().snapshot();
  writeSettings({ hasSeenWelcome: snapshot.state.hasSeenWelcome });
  await putSnapshot(snapshot);
}

export function exportSave(): void {
  const snapshot = useGameStore.getState().snapshot();
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `the-bees-hive-day-${snapshot.state.time.day}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function importSave(file: File): Promise<void> {
  const text = await file.text();
  const snapshot = JSON.parse(text) as GameSnapshot;

  if (snapshot.version !== 1) {
    throw new Error('Unsupported save file version');
  }

  useGameStore.getState().replaceFromSnapshot(snapshot);
  await putSnapshot(snapshot);
}
